import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { HelpCircle, Calendar, Trash2 } from 'lucide-react';

export const SupportManager = () => {
  const { getAuthHeader } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/support`, {
        headers: getAuthHeader(),
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching support submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeader]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(
        `${API_URL}/api/support/${id}`,
        null,
        {
          params: { status },
          headers: getAuthHeader(),
        }
      );
      fetchSubmissions();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this support message?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/support/${id}`, {
        headers: getAuthHeader(),
      });
      fetchSubmissions();
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Failed to delete submission');
    }
  };

  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Support Requests</h2>
        <p className="text-gray-400">Customer support inquiries and help requests</p>
      </div>

      {submissions.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 text-center">
          <HelpCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No support requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <div
              key={submission.id}
              className="bg-gray-800 border border-gray-700 rounded-lg p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">{submission.name}</h3>
                  <p className="text-gray-400">{submission.email}</p>
                  <p className="text-orange-500 font-medium mt-1">{submission.subject}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={submission.status}
                    onChange={(e) => updateStatus(submission.id, e.target.value)}
                    className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  >
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="responded">Responded</option>
                  </select>
                  <span
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      submission.status === 'new'
                        ? 'bg-green-500 text-white'
                        : submission.status === 'read'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-500 text-white'
                    }`}
                  >
                    {submission.status}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-white">{submission.message}</p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center text-gray-400 text-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  {new Date(submission.created_at).toLocaleString()}
                </div>
                <button
                  onClick={() => deleteSubmission(submission.id)}
                  className="flex items-center px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};